import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit } from "lucide-react";
import { RELAZIONI, STATI_EMOTIVI, EMOZIONI, STILI_RISPOSTA } from "./SchedaConstants";

const SummarySection = ({ title, children }) => (
  <div className="border-b border-gray-100 pb-3 mb-3 last:border-0">
    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
    {children}
  </div>
);

export const SchedaSummaryView = ({ episode, onBack, onEdit }) => {
  const ep = episode;

  const getEmozioniList = () => {
    if (!ep.emozioni || Object.keys(ep.emozioni).length === 0) return null;
    return Object.entries(ep.emozioni).map(([id, intensity]) => {
      const em = EMOZIONI.find(e => e.id === id);
      return em ? `${em.emoji} ${em.label} (${intensity}/10)` : null;
    }).filter(Boolean).join(", ");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
        <Button variant="outline" size="sm" onClick={onEdit} className="rounded-full">
          <Edit className="w-4 h-4 mr-1" /> Modifica
        </Button>
      </div>

      <Card className="border-2 border-orange-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Riepilogo Episodio</CardTitle>
            <Badge variant="outline">{new Date(ep.data).toLocaleDateString('it-IT')}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-0">
          <SummarySection title="Contesto">
            <div className="grid grid-cols-2 gap-2 text-sm">
              {ep.conChi && <p><span className="text-gray-500">Con:</span> {ep.conChi}</p>}
              {ep.relazione && <p><span className="text-gray-500">Relazione:</span> {RELAZIONI.find(r => r.id === ep.relazione)?.label}</p>}
              {ep.luogo && <p><span className="text-gray-500">Luogo:</span> {ep.luogo}</p>}
              {ep.tema && <p><span className="text-gray-500">Tema:</span> {ep.tema}</p>}
            </div>
          </SummarySection>

          {ep.descrizione && (
            <SummarySection title="Cosa \u00e8 accaduto">
              <p className="text-sm text-gray-800">{ep.descrizione}</p>
            </SummarySection>
          )}

          {(ep.ioDetto?.some(d => d) || ep.altroDetto?.some(d => d)) && (
            <SummarySection title="Dialoghi">
              {ep.ioDetto?.filter(d => d).length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-blue-600 font-medium mb-1">IO:</p>
                  {ep.ioDetto.filter(d => d).map((d, i) => (
                    <p key={`io-detto-${i}-${d.substring(0,10)}`} className="text-sm text-gray-700 italic">"{d}"</p>
                  ))}
                </div>
              )}
              {ep.altroDetto?.filter(d => d).length > 0 && (
                <div>
                  <p className="text-xs text-orange-600 font-medium mb-1">L'ALTRO:</p>
                  {ep.altroDetto.filter(d => d).map((d, i) => (
                    <p key={`altro-detto-${i}-${d.substring(0,10)}`} className="text-sm text-gray-700 italic">"{d}"</p>
                  ))}
                </div>
              )}
            </SummarySection>
          )}

          {(ep.ioStatoEmotivo || ep.altroStatoEmotivo) && (
            <SummarySection title="Non verbale">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {(ep.ioStatoEmotivo || ep.ioVolume || ep.ioRitmo) && (
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <p className="text-xs font-medium text-blue-700 mb-1">IO</p>
                    {ep.ioStatoEmotivo && <p>Stato: {STATI_EMOTIVI.find(s => s.id === ep.ioStatoEmotivo)?.label}</p>}
                    {ep.ioVolume && <p>Volume: {ep.ioVolume}</p>}
                    {ep.ioRitmo && <p>Ritmo: {ep.ioRitmo}</p>}
                    {ep.ioPostura && <p>Postura: {ep.ioPostura}</p>}
                  </div>
                )}
                {(ep.altroStatoEmotivo || ep.altroVolume || ep.altroRitmo) && (
                  <div className="bg-orange-50 p-2 rounded-lg">
                    <p className="text-xs font-medium text-orange-700 mb-1">L'ALTRO</p>
                    {ep.altroStatoEmotivo && <p>Stato: {STATI_EMOTIVI.find(s => s.id === ep.altroStatoEmotivo)?.label}</p>}
                    {ep.altroVolume && <p>Volume: {ep.altroVolume}</p>}
                    {ep.altroRitmo && <p>Ritmo: {ep.altroRitmo}</p>}
                    {ep.altroPostura && <p>Postura: {ep.altroPostura}</p>}
                  </div>
                )}
              </div>
            </SummarySection>
          )}

          {getEmozioniList() && (
            <SummarySection title="Come mi sono sentito">
              <p className="text-sm text-gray-800">{getEmozioniList()}</p>
              {ep.sensazioniFisiche && <p className="text-sm text-gray-600 mt-1">Sensazioni: {ep.sensazioniFisiche}</p>}
            </SummarySection>
          )}

          {ep.pensieri?.some(p => p) && (
            <SummarySection title="Pensieri">
              {ep.pensieri.filter(p => p).map((p, i) => (
                <p key={`pensiero-${i}-${p.substring(0,10)}`} className="text-sm text-gray-700 italic">"{p}"</p>
              ))}
            </SummarySection>
          )}

          {(ep.cosaVolevo || ep.cosaOttenuto) && (
            <SummarySection title="Obiettivi e risultati">
              {ep.cosaVolevo && <p className="text-sm"><span className="text-gray-500">Volevo:</span> {ep.cosaVolevo}</p>}
              {ep.cosaOttenuto && <p className="text-sm"><span className="text-gray-500">Ottenuto:</span> {ep.cosaOttenuto}</p>}
            </SummarySection>
          )}

          {(ep.mioStile || ep.altroStile) && (
            <SummarySection title="Stile comunicativo">
              <div className="flex gap-3">
                {ep.mioStile && (
                  <Badge className={STILI_RISPOSTA.find(s => s.id === ep.mioStile)?.color}>
                    IO: {STILI_RISPOSTA.find(s => s.id === ep.mioStile)?.label}
                  </Badge>
                )}
                {ep.altroStile && (
                  <Badge className={STILI_RISPOSTA.find(s => s.id === ep.altroStile)?.color}>
                    ALTRO: {STILI_RISPOSTA.find(s => s.id === ep.altroStile)?.label}
                  </Badge>
                )}
              </div>
            </SummarySection>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
